import { useCallback, useEffect, useState } from 'react'

import styles from './PauseMenu.module.css'
import type { Language } from './Screens.js'
import { useFullscreen } from './useFullscreen.js'

/**
 * Nabídka během kurzu — pauza, nápověda, ukončení.
 *
 * Náhrada původního `PauseMenu.js`. Rozdíly:
 *  - ikony jsou inline SVG, ne knihovna (`react-icons` byl kvůli tomuhle
 *    v původní aplikaci celý závislostí navíc)
 *  - dotykové cíle mají 44 px a respektují bezpečnou zónu telefonu
 *  - pauza zastavuje i časomíru, takže uživatele nestojí body
 *  - ovladatelné klávesnicí, Esc zavírá
 */

const TEXT = {
  cze: {
    menu: 'Nabídka',
    pause: 'Pozastavit',
    resume: 'Pokračovat',
    help: 'Nápověda',
    quit: 'Ukončit kurz',
    paused: 'Kurz je pozastaven',
    pausedNote: 'Časomíra stojí, o body nepřicházíte.',
    helpTitle: 'Nápověda',
    helpItems: [
      'Vyberte jednu z nabízených odpovědí.',
      'Po odpovědi se pokračuje samo; klepnutím to urychlíte.',
      'Špatně zodpovězená otázka se později zopakuje.',
      'Hodiny vlevo nahoře upozorňují na blížící se časový limit.',
      'Postup se průběžně ukládá — po obnovení stránky budete pokračovat.',
    ],
    // Ukáže se jen tam, kde prohlížeč celou obrazovku neumí (iPhone — Apple
    // nutí i Chrome a Firefox používat WebKit, takže platí pro všechny).
    noFullscreen:
      'Celá obrazovka na iPhonu nejde zapnout tlačítkem. Přes nabídku sdílení zvolte „Přidat na plochu“ a kurz pak spusťte z ikony — poběží bez lišty prohlížeče.',
    quitTitle: 'Opravdu ukončit kurz?',
    quitNote: 'Rozdělaný postup zůstane uložený, můžete se k němu vrátit.',
    quitConfirm: 'Ukončit',
    cancel: 'Zpět do kurzu',
    close: 'Zavřít',
    enterFullscreen: 'Celá obrazovka',
    exitFullscreen: 'Ukončit celou obrazovku',
    openMenu: 'Nabídka',
    closeMenu: 'Zavřít nabídku',
  },
  eng: {
    menu: 'Menu',
    pause: 'Pause',
    resume: 'Resume',
    help: 'Help',
    quit: 'Quit course',
    paused: 'Course paused',
    pausedNote: 'The timer is stopped, you are not losing points.',
    helpTitle: 'Help',
    helpItems: [
      'Choose one of the offered answers.',
      'After answering it continues on its own; tap to skip ahead.',
      'A wrong answer means the question comes back later.',
      'The clock in the top left warns about the time limit.',
      'Your progress is saved — reloading the page will resume it.',
    ],
    noFullscreen:
      'Fullscreen cannot be enabled with a button on iPhone. Use the share menu, choose “Add to Home Screen”, and start the course from the icon — it will run without the browser bar.',
    quitTitle: 'Really quit the course?',
    quitNote: 'Your progress stays saved and you can come back to it.',
    quitConfirm: 'Quit',
    cancel: 'Back to course',
    close: 'Close',
    enterFullscreen: 'Fullscreen',
    exitFullscreen: 'Exit fullscreen',
    openMenu: 'Menu',
    closeMenu: 'Close menu',
  },
} as const

type Panel = 'none' | 'help' | 'quit'

export interface PauseMenuProps {
  paused: boolean
  /**
   * Nabízet v railu přepínač pauzy?
   *
   * U videa ne — pauza tam patří do spodního pruhu k časové ose. V railu
   * uprostřed pravého okraje by ležela přímo v obraze. Dvě tlačítka pro
   * jednu věc nesmí existovat, takže si je obě místa nesmějí nárokovat.
   */
  showPauseToggle?: boolean
  onPause: () => void
  onResume: () => void
  onQuit: () => void
  language: Language
}

export function PauseMenu({
  paused,
  onPause,
  onResume,
  onQuit,
  language,
  showPauseToggle = true,
}: PauseMenuProps): React.ReactElement {
  const t = TEXT[language]
  const [panel, setPanel] = useState<Panel>('none')
  const [open, setOpen] = useState(false)
  const fullscreen = useFullscreen()

  const closePanel = useCallback(() => setPanel('none'), [])

  // Esc zavře otevřený dialog, jinak sbalí nabídku
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return
      if (panel !== 'none') closePanel()
      else if (open) setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [panel, open, closePanel])

  const openHelp = useCallback(() => {
    setPanel('help')
    setOpen(false)
    onPause()
  }, [onPause])

  const openQuit = useCallback(() => {
    setPanel('quit')
    setOpen(false)
    onPause()
  }, [onPause])

  const back = useCallback(() => {
    setPanel('none')
    onResume()
  }, [onResume])

  const togglePause = useCallback(() => {
    setOpen(false)
    if (paused) onResume()
    else onPause()
  }, [paused, onPause, onResume])

  // Každá akce nabídku sbalí — jinak zůstane viset přes obsah
  const toggleFullscreen = useCallback(() => {
    setOpen(false)
    fullscreen.toggle()
  }, [fullscreen])

  return (
    <>
      <div className={styles.root}>
        <div className={styles.row}>
          {/*
            Popisek pauzy, NE tlačítko.
            Dřív to bylo druhé tlačítko dělající totéž co přepínač vedle něj.
            Vedle sebe stály dva ovladače pro jednu věc a jejich naskočení
            navíc posunulo řádek pod kurzorem — druhé kliknutí pak trefilo
            přepínač a kurz se rozjel. Stav hlásí popisek, ovládá přepínač.
          */}
          {paused && panel === 'none' && (
            <span className={styles.pausedBadge} data-testid="paused-badge">
              {t.paused}
            </span>
          )}

          {showPauseToggle && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={togglePause}
              aria-label={paused ? t.resume : t.pause}
              title={paused ? t.resume : t.pause}
            >
              {paused ? <IconPlay /> : <IconPause />}
            </button>
          )}

          <button
            type="button"
            className={styles.iconButton}
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t.closeMenu : t.openMenu}
            aria-expanded={open}
            title={open ? t.closeMenu : t.openMenu}
          >
            <IconGear />
          </button>
        </div>

        {open && (
          <div className={styles.tray}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={openHelp}
              aria-label={t.help}
              title={t.help}
            >
              <IconHelp />
            </button>
            {/* Skryté tam, kde API není — typicky iOS Safari. */}
            {fullscreen.supported && (
              <button
                type="button"
                className={styles.iconButton}
                onClick={toggleFullscreen}
                aria-label={fullscreen.active ? t.exitFullscreen : t.enterFullscreen}
                title={fullscreen.active ? t.exitFullscreen : t.enterFullscreen}
              >
                {fullscreen.active ? <IconExitFullscreen /> : <IconFullscreen />}
              </button>
            )}
            <button
              type="button"
              className={styles.iconButton}
              onClick={openQuit}
              aria-label={t.quit}
              title={t.quit}
            >
              <IconPower />
            </button>
          </div>
        )}
      </div>

      {panel === 'help' && (
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-label={t.helpTitle}>
          <div className={styles.panel}>
            <h2 className={styles.panelTitle}>{t.helpTitle}</h2>
            <ul className={styles.helpList}>
              {t.helpItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
              {/* Vysvětlí chybějící tlačítko celé obrazovky místo mlčení. */}
              {!fullscreen.supported && <li>{t.noFullscreen}</li>}
            </ul>
            <div className={styles.panelActions}>
              <button type="button" className={styles.action} onClick={back} autoFocus>
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {panel === 'quit' && (
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-label={t.quitTitle}>
          <div className={styles.panel}>
            <h2 className={styles.panelTitle}>{t.quitTitle}</h2>
            <p className={styles.panelText}>{t.quitNote}</p>
            <div className={styles.panelActions}>
              <button type="button" className={styles.action} onClick={back} autoFocus>
                {t.cancel}
              </button>
              <button
                type="button"
                className={`${styles.action} ${styles.danger}`}
                onClick={onQuit}
              >
                {t.quitConfirm}
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  )
}

/* --- ikony: inline SVG, žádná knihovna ------------------------------- */

function IconPause(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  )
}

function IconPlay(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86A1 1 0 0 0 8 5.14z" />
    </svg>
  )
}

function IconHelp(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm.9 15.5h-1.8v-1.8h1.8v1.8zm1.9-6.9-.8.8c-.7.7-1.1 1.2-1.1 2.4h-1.8v-.4c0-1.2.5-1.9 1.2-2.6l1.1-1.1c.3-.3.5-.8.5-1.2a2 2 0 0 0-4 0H8.1a3.9 3.9 0 1 1 7.8 0c0 .8-.3 1.6-.9 2.1z" />
    </svg>
  )
}

function IconGear(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19.4 13a7.8 7.8 0 0 0 0-2l2.1-1.6a.5.5 0 0 0 .1-.6l-2-3.5a.5.5 0 0 0-.6-.2l-2.5 1a7.6 7.6 0 0 0-1.7-1l-.4-2.6a.5.5 0 0 0-.5-.4h-4a.5.5 0 0 0-.5.4l-.4 2.6c-.6.3-1.2.6-1.7 1l-2.5-1a.5.5 0 0 0-.6.2l-2 3.5a.5.5 0 0 0 .1.6L4.6 11a7.8 7.8 0 0 0 0 2l-2.1 1.6a.5.5 0 0 0-.1.6l2 3.5c.1.2.4.3.6.2l2.5-1c.5.4 1.1.7 1.7 1l.4 2.6c0 .2.2.4.5.4h4c.3 0 .5-.2.5-.4l.4-2.6c.6-.3 1.2-.6 1.7-1l2.5 1c.2.1.5 0 .6-.2l2-3.5a.5.5 0 0 0-.1-.6L19.4 13zM12 15.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7z" />
    </svg>
  )
}

function IconFullscreen(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 9V4h5v2H6v3H4zm11-5h5v5h-2V6h-3V4zM4 15h2v3h3v2H4v-5zm14 0h2v5h-5v-2h3v-3z" />
    </svg>
  )
}

function IconExitFullscreen(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 4v5H4V7h3V4h2zm6 0h2v3h3v2h-5V4zM4 15h5v5H7v-3H4v-2zm11 0h5v2h-3v3h-2v-5z" />
    </svg>
  )
}

function IconPower(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13 3h-2v10h2V3zm4.8 2.1-1.4 1.4A6.98 6.98 0 0 1 19 12a7 7 0 1 1-14 0c0-2.2 1-4.2 2.6-5.5L6.2 5.1A8.96 8.96 0 0 0 3 12a9 9 0 1 0 18 0c0-2.8-1.3-5.3-3.2-6.9z" />
    </svg>
  )
}
